/**
 * EventSourceService
 * Events hub source connections (Planning Center Calendar): settings, test, sync now, run history
 * and the per-source location / tag mappings with suggestions.
 * Route: events/api/events/sources (Admin role OR the Event.Source.* / Event.Mapping.Manage right)
 */
import { BaseService } from '../base/BaseService';
import { unwrapEventsResponse as unwrap } from './unwrapEventsResponse';
export class EventSourceService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? EventSourceService.DEFAULT_SERVICE_PATH,
            basePath: EventSourceService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- connections ----
    /** GET api/events/sources (Event.Source.View). */
    async list() {
        const res = await this.listRaw();
        return res.data ?? [];
    }
    listRaw() {
        return this.http.get(this.buildUrl());
    }
    /** GET api/events/sources/{id} (Event.Source.View). */
    async get(id) {
        return unwrap(await this.getRaw(id), `Event source ${id} not found`);
    }
    getRaw(id) {
        return this.http.get(this.buildUrl(String(id)));
    }
    /** PUT api/events/sources/{id} (Event.Source.Manage). Missing fields are left as they are. */
    async update(id, request) {
        return unwrap(await this.updateRaw(id, request), `Could not update event source ${id}`);
    }
    updateRaw(id, request) {
        return this.http.put(this.buildUrl(String(id)), request);
    }
    /** POST api/events/sources/{id}/test (Event.Source.Sync). */
    async test(id) {
        return unwrap(await this.testRaw(id), `Could not test event source ${id}`);
    }
    testRaw(id) {
        return this.http.post(this.buildUrl(`${id}/test`));
    }
    /** POST api/events/sources/{id}/sync (Event.Source.Sync). Queues a manual run (202). */
    async sync(id) {
        const res = await this.syncRaw(id);
        return res.data ?? { queued: res.success !== false };
    }
    syncRaw(id) {
        return this.http.post(this.buildUrl(`${id}/sync`));
    }
    /** GET api/events/sources/{id}/runs?take= (Event.Source.View). Newest first. */
    async getRuns(id, take = 20) {
        const res = await this.getRunsRaw(id, take);
        return res.data ?? [];
    }
    getRunsRaw(id, take = 20) {
        return this.http.get(this.buildUrlWithParams(`${id}/runs`, { take }));
    }
    // ---- mappings ----
    /** GET api/events/sources/{id}/mappings?kindId&statusId (Event.Source.View). */
    async getMappings(id, query = {}) {
        const res = await this.getMappingsRaw(id, query);
        return res.data ?? [];
    }
    getMappingsRaw(id, query = {}) {
        return this.http.get(this.buildUrlWithParams(`${id}/mappings`, { kindId: query.kindId, statusId: query.statusId }));
    }
    /** PUT api/events/sources/{id}/mappings/{mappingId} (Event.Mapping.Manage). Re-resolves held events. */
    async updateMapping(id, mappingId, request) {
        return unwrap(await this.updateMappingRaw(id, mappingId, request), `Could not update mapping ${mappingId}`);
    }
    updateMappingRaw(id, mappingId, request) {
        return this.http.put(this.buildUrl(`${id}/mappings/${mappingId}`), request);
    }
    /** GET api/events/sources/{id}/mappings/suggestions (Event.Source.View). */
    async getSuggestions(id) {
        const res = await this.getSuggestionsRaw(id);
        return res.data ?? [];
    }
    getSuggestionsRaw(id) {
        return this.http.get(this.buildUrl(`${id}/mappings/suggestions`));
    }
}
EventSourceService.DEFAULT_SERVICE_PATH = 'events';
EventSourceService.BASE_PATH = 'api/events/sources';
//# sourceMappingURL=EventSourceService.js.map